import React, { useEffect, useMemo, useState } from 'react';
import recommend from '@algolia/recommend';
import { useInView } from 'react-intersection-observer';
import { Order } from 'shared/types/cart';
import { ProjectSettings } from 'shared/types/ProjectSettings';
import SwiperCore, { FreeMode, Navigation, Pagination } from 'swiper';
import { lazyloadconfig } from '@/helpers/utils/brandButton';
import { routerUrl } from '@/helpers/utils/routerUrl';
import { useProjSettings } from '@/providers/projectSettings/hooks/useProjectSettings';
import { useCart } from 'frontastic';
import AlgoliaRecommendProductsList from './algoliaRecommendProductsList';

SwiperCore.use([Pagination, Navigation, FreeMode]);

interface RelatedItemsContentProps {
  siteId?: string;
  maxRelatedProductsPerPage: number | undefined;
  data: any;
  setProductsHave: (haveProducts: boolean) => void;
}
const RelatedItemsContent: React.FC<RelatedItemsContentProps> = ({
  siteId,
  maxRelatedProductsPerPage,
  data,
  setProductsHave,
}) => {
  const { data: cart } = useCart();
  const projectSettings: ProjectSettings = useProjSettings();
  const [relatedItems, setRelatedItems] = useState<any>(undefined);
  const [inViewState, setInViewState] = useState<boolean>(false);
  const objectID = data?.data?.productListCarousel?.dataSource?.product?.productId ?? data?.data?.nonPdpProductId;
  const pageType = data?.data?.productListCarousel?.dataSource?.pageType;

  useEffect(() => {
    setInViewState(true);
  }, []);

  const { ref, inView } = useInView({
    rootMargin: lazyloadconfig.rootMargin,
    triggerOnce: true,
  });

  const objectIDs: string[] = useMemo(() => {
    const normalizedPageType = `/${pageType}`;

    if (normalizedPageType === routerUrl.cart && cart?.lineItems) {
      return cart.lineItems
        .map((lineItem) => lineItem.productKey)
        .filter((productKey): productKey is string => productKey !== undefined);
    }

    if (normalizedPageType === routerUrl.thankYou) {
      const order = data?.data?.productListCarousel?.dataSource?.order as Order;
      if (order?.lineItems) {
        return order.lineItems
          .map((lineItem) => lineItem.productKey)
          .filter((productKey): productKey is string => productKey !== undefined);
      }
    }

    return objectID ? [objectID] : [];
  }, [pageType, cart?.lineItems, objectID, data]);

  useEffect(() => {
    if (!siteId || !projectSettings?.algoliaAppId || !projectSettings?.algoliaApiKey || objectIDs.length === 0) {
      setProductsHave(false);
      return;
    }

    const recommendClient = recommend(projectSettings?.algoliaAppId, projectSettings?.algoliaApiKey);
    if (objectIDs.length > 0 && recommendClient && inView) {
      const recommendationQueries = objectIDs.map((id) => ({
        queryParameters: {
          attributesToRetrieve: ['*'],
          analytics: false,
          enableABTest: false,
          enableRules: true,
          getRankingInfo: false,
          enablePersonalization: true,
          filters: 'isInStock:true',
        },
        maxRecommendations: maxRelatedProductsPerPage ?? 15,
        model: 'related-products',
        indexName: `products_${siteId}`,
        objectID: `${id}`,
        threshold: 0,
      }));

      recommendClient
        .getRelatedProducts(recommendationQueries)
        .then((recommendations: any) => {
          if (recommendations?.results) {
            const relatedItems: any[] = [];
            recommendations?.results
              ?.filter((item: any, index: any) => {
                return recommendations?.results?.indexOf(item) === index;
              })
              ?.forEach((item: any) => {
                if (item?.hits && item?.hits.length > 0) {
                  relatedItems.push(...item.hits);
                }
              });
            setRelatedItems(relatedItems);
            setProductsHave(relatedItems?.length > 0);
          }
        })
        .catch((error) => {
          console.error('Error while fetching data:', error);
          setProductsHave(false);
          setRelatedItems(undefined);
        });
    }
  }, [objectIDs, projectSettings, siteId, inView]);

  return (
    <section ref={ref}>
      {relatedItems && (inView || !inViewState) && <AlgoliaRecommendProductsList item={relatedItems} />}
    </section>
  );
};

export default RelatedItemsContent;
